import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { listCompanies } from '../services/api';
import ScoreStatCard from './ScoreStatCard';
import '../styles/companies.css';

const clampScore = (value) => {
  if (value == null || Number.isNaN(value)) return null;
  return Math.max(0, Math.min(100, Number(value)));
};

const formatName = (name) => {
  if (!name) return 'Unknown';
  return name.charAt(0).toUpperCase() + name.slice(1);
};

const getVerificationIndicator = (verificationLevel) => {
  if (verificationLevel === 'fully_verified') {
    return { title: 'Fully verified', className: 'status-indicator indicator-verified-full' };
  }

  if (verificationLevel === 'stage_1_verified') {
    return { title: 'Stage 1 verified', className: 'status-indicator indicator-verified-stage1' };
  }

  return { title: 'Not verified', className: 'status-indicator indicator-verified-none' };
};

const parsePageParam = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const scrollToTop = () => {
  const mainContent = document.querySelector('.companies-main');
  if (mainContent) {
    mainContent.scrollTop = 0;
    mainContent.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }

  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
};

function CompaniesList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('verification_level');
  const [page, setPage] = useState(() => parsePageParam(searchParams.get('page')));
  const [pageSize] = useState(24);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, orderBy]);

  useEffect(() => {
    const urlPage = parsePageParam(searchParams.get('page'));
    setPage((prev) => (prev === urlPage ? prev : urlPage));
  }, [searchParams]);

  useEffect(() => {
    const currentUrlPage = parsePageParam(searchParams.get('page'));
    if (currentUrlPage === page) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    if (page <= 1) {
      nextParams.delete('page');
    } else {
      nextParams.set('page', String(page));
    }

    setSearchParams(nextParams, { replace: true });
  }, [page, searchParams, setSearchParams]);

  useEffect(() => {
    scrollToTop();
    requestAnimationFrame(scrollToTop);
  }, [page]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    const timer = setTimeout(() => {
      listCompanies({ search, orderBy, page, pageSize })
        .then((data) => {
          if (isMounted) {
            setCompanies(data.companies || []);
            setTotal(data.total || 0);
          }
        })
        .catch((err) => {
          console.error('Error loading companies:', err);
          if (isMounted) {
            setCompanies([]);
          }
        })
        .finally(() => {
          if (isMounted) {
            setLoading(false);
          }
        });
    }, 250);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [search, orderBy, page, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startIndex = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIndex = Math.min(total, page * pageSize);

  return (
    <div className="companies-page">
      <Header />

      <main className="companies-main">
        <div className="companies-header">
          <div className="companies-title-block">
            <h1>All Companies</h1>
            <p>Browse the network and compare security posture across organizations.</p>
            <div className="companies-verification-legend" aria-label="Company verification legend">
              <div className="verification-legend-item">
                <span className="status-indicator indicator-verified-full" aria-hidden="true" />
                <span>
                  <strong>Green:</strong> fully verified. The Risqly badge is displayed on the organization&apos;s website and verified.
                </span>
              </div>
              <div className="verification-legend-item">
                <span className="status-indicator indicator-verified-stage1" aria-hidden="true" />
                <span>
                  <strong>Orange:</strong> stage 1 verified. Someone has logged in for the company domain and provided their vendors.
                </span>
              </div>
              <div className="verification-legend-item">
                <span className="status-indicator indicator-verified-none" aria-hidden="true" />
                <span>
                  <strong>Red:</strong> not verified.
                </span>
              </div>
            </div>
          </div>
          <div className="companies-controls">
            <div className="companies-search">
              <span className="material-symbols-outlined">search</span>
              <input
                type="text"
                placeholder="Search companies..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <div className="companies-sort">
              <label htmlFor="companies-sort">Order by</label>
              <select
                id="companies-sort"
                value={orderBy}
                onChange={(event) => setOrderBy(event.target.value)}
              >
                <option value="verification_level">Verification Level</option>
                <option value="final_score">Security Score</option>
                <option value="policy_score">Policy Compliance</option>
                <option value="outside_in_score">External Integrity</option>
                <option value="transparency">Transparency</option>
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="companies-loading">Loading companies...</div>
        ) : companies.length === 0 ? (
          <div className="companies-empty">
            <h2>No companies found</h2>
            <p>Try a different search or sorting option.</p>
          </div>
        ) : (
          <div className="companies-grid">
            {companies.map((company) => {
              const finalScore = clampScore(company.final_score);
              const transparency = company.transparency == null ? null : Math.round(company.transparency * 100);
              const policyScore = clampScore(company.policy_score);
              const outsideScore = clampScore(company.outside_in_score);
              const verificationIndicator = getVerificationIndicator(company.verification_level);

              return (
                <div
                  key={company.id}
                  className="company-card is-clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/company/${encodeURIComponent(company.normalized_name)}`)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      navigate(`/company/${encodeURIComponent(company.normalized_name)}`);
                    }
                  }}
                >
                  <div className="company-card-top">
                    <div className="company-card-title-section">
                      <h3>
                        <span className="company-card-name">{formatName(company.normalized_name)}</span>
                      </h3>
                      <span className="company-domain">{company.domain || 'No domain'}</span>
                    </div>
                    <div className="company-status-row">
                      <span
                        className={verificationIndicator.className}
                        title={`Verification: ${verificationIndicator.title}`}
                        role="img"
                        aria-label={`Verification status: ${verificationIndicator.title}`}
                      />
                    </div>
                  </div>

                  <div className="company-scores">
                    <ScoreStatCard
                      variant="compact"
                      title="Security Score"
                      value={finalScore}
                      ringValue={finalScore}
                      color="#00ff41"
                    />

                    <ScoreStatCard
                      variant="compact"
                      title="Transparency"
                      value={transparency}
                      ringValue={transparency}
                      color="#ff9500"
                      suffix="%"
                      showMax={false}
                    />

                    <ScoreStatCard
                      variant="compact"
                      title="Policy"
                      value={policyScore}
                      ringValue={policyScore}
                      color="#00bcd4"
                    />

                    <ScoreStatCard
                      variant="compact"
                      title="External Integrity"
                      value={outsideScore}
                      ringValue={outsideScore}
                      color="#e91e63"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && total > 0 && (
          <div className="companies-pagination">
            <div className="companies-pagination-info">
              Showing {startIndex} - {endIndex} of {total}
            </div>
            <div className="companies-pagination-controls">
              <button
                className="btn btn-secondary"
                type="button"
                disabled={page <= 1}
                onClick={() => {
                  scrollToTop();
                  setPage((prev) => Math.max(1, prev - 1));
                }}
              >
                Previous
              </button>
              <span className="companies-pagination-page">
                Page {page} of {totalPages}
              </span>
              <button
                className="btn btn-secondary"
                type="button"
                disabled={page >= totalPages}
                onClick={() => {
                  scrollToTop();
                  setPage((prev) => Math.min(totalPages, prev + 1));
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default CompaniesList;
